<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { useGuardrailsStore } from '../../stores/guardrailsStore';

const store = useGuardrailsStore();
const { t } = useI18n();

function toggle() {
    if (store.enabled === null || store.saveStatus === 'saving') return;
    void store.setEnabled(!(store.enabled === true));
}
</script>

<template>
    <div v-if="store.isOpen" class="gr-overlay" @click.self="store.close()">
        <div class="gr-modal">
            <div class="gr-modal-close-strip">
                <button class="gr-modal-close" type="button" @click="store.close()">
                    <span class="material-icons" style="font-size: 18px">close</span>
                    <span>{{ t('common.close') }}</span>
                </button>
            </div>

            <div class="gr-modal-header">
                <span class="material-icons gr-modal-header-icon">security</span>
                <span class="gr-modal-title">{{ t('guardrails.title') }}</span>
            </div>

            <div class="gr-modal-body">
                <p class="gr-description">{{ t('guardrails.description') }}</p>

                <div class="gr-toggle-row">
                    <div class="gr-toggle-text">
                        <div class="gr-toggle-label">{{ t('guardrails.toggleLabel') }}</div>
                        <div class="gr-toggle-status">
                            {{
                                store.enabled === null
                                    ? t('guardrails.loading')
                                    : store.enabled
                                      ? t('guardrails.statusOn')
                                      : t('guardrails.statusOff')
                            }}
                        </div>
                    </div>
                    <button
                        class="gr-switch"
                        :class="{ on: store.enabled === true }"
                        type="button"
                        role="switch"
                        :aria-checked="store.enabled === true"
                        :aria-label="t('guardrails.toggleLabel')"
                        :disabled="store.enabled === null || store.saveStatus === 'saving'"
                        @click="toggle"
                    >
                        <span class="gr-switch-knob"></span>
                    </button>
                </div>

                <div v-if="store.saveStatus === 'error'" class="gr-error">
                    <span class="material-icons gr-error-icon">error_outline</span>
                    <span>{{ t('guardrails.saveFailed') }}</span>
                </div>
            </div>

            <div class="gr-modal-footer">
                <button
                    class="gr-footer-btn gr-footer-btn-primary"
                    type="button"
                    @click="store.close()"
                >
                    {{ t('common.close') }}
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.gr-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.25);
    z-index: var(--z-modal-elevated);
    display: flex;
    align-items: center;
    justify-content: center;
}

.gr-modal {
    width: 440px;
    background: var(--color-bg-white);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-lg);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.gr-modal-close-strip {
    padding: 6px 14px;
    border-bottom: 1px solid var(--color-border);
}

.gr-modal-close {
    background: transparent;
    border: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: var(--text-base);
    color: var(--color-text-primary);
    cursor: pointer;
    padding: 4px 8px;
}

.gr-modal-close:hover {
    color: var(--color-primary);
}

.gr-modal-header {
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    border-bottom: 1px solid var(--color-border);
    background: var(--color-header-bg);
}

.gr-modal-header-icon {
    font-size: 20px;
    color: var(--color-text-secondary);
}

.gr-modal-title {
    font-size: var(--text-base);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
}

.gr-modal-body {
    padding: 20px 20px 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.gr-description {
    font-size: var(--text-sm);
    color: var(--color-text-secondary);
    line-height: 1.5;
    margin: 0;
}

.gr-toggle-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 14px;
    background: var(--color-bg-subtle, #f8f9fa);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
}

.gr-toggle-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.gr-toggle-label {
    font-size: var(--text-sm);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
}

.gr-toggle-status {
    font-size: var(--text-sm);
    color: var(--color-text-secondary);
}

.gr-switch {
    width: 44px;
    height: 24px;
    border-radius: 999px;
    background: var(--color-border);
    position: relative;
    cursor: pointer;
    border: none;
    flex-shrink: 0;
    transition: background 0.15s;
}

.gr-switch.on {
    background: var(--color-primary);
}

.gr-switch:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.gr-switch-knob {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #fff;
    transition: transform 0.15s;
}

.gr-switch.on .gr-switch-knob {
    transform: translateX(20px);
}

.gr-error {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--text-sm);
    color: var(--color-error, #c62828);
}

.gr-error-icon {
    font-size: 18px;
}

.gr-modal-footer {
    padding: 12px 20px 16px;
    border-top: 1px solid var(--color-border);
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.gr-footer-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 20px;
    border: 1px solid transparent;
    border-radius: var(--radius-md);
    font-size: var(--text-sm);
    font-weight: var(--font-weight-medium);
    cursor: pointer;
    transition: opacity 0.15s, background 0.15s;
}

.gr-footer-btn-primary {
    background: var(--color-primary);
    color: #fff;
}

.gr-footer-btn-primary:hover {
    opacity: 0.88;
}
</style>
